import PageHero from "@/components/site/PageHero";
import Reveal from "@/components/site/Reveal";
import { Link } from "react-router-dom";
import { Instagram, Linkedin, Facebook, MapPin, Mail, Phone } from "lucide-react";
import { COMPANY, EXHIBITION_IMAGES } from "@/data/content";
import { useT, useRoute } from "@/i18n";

export default function About() {
  const t = useT();
  const route = useRoute();
  return (
    <div data-testid="about-page" className="bg-white">
      <PageHero
        eyebrow={t.pages.about.eyebrow}
        title={t.pages.about.title}
        subtitle={t.pages.about.subtitle}
        image={EXHIBITION_IMAGES[0]}
      />

      <section className="py-24 md:py-32">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10">
          <Reveal>
            <p className="eyebrow">{t.pages.about.sectionEyebrow}</p>
            <h2 className="heading-display mt-4 text-4xl sm:text-5xl lg:text-6xl uppercase">
              {t.pages.about.heading1}{" "}
              <span className="text-[#C20E1A]">{t.pages.about.heading2}</span>
              <br />
              {t.pages.about.heading3}
            </h2>
          </Reveal>

          <div className="mt-12 space-y-6 text-lg text-black/80 leading-relaxed">
            {t.pages.about.paragraphs.map((p, i) => (
              <Reveal key={i} delay={i * 40}>
                <p>{p}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Visit Us */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          <Reveal>
            <p className="eyebrow">{t.pages.about.visitEyebrow}</p>
            <h2 className="heading-display mt-4 text-5xl sm:text-6xl uppercase">
              {t.pages.about.visitTitle1}{" "}
              <span className="text-[#C20E1A]">{t.pages.about.visitTitle2}</span>
            </h2>
            <div className="mt-8 space-y-4 text-base text-black/80">
              <p className="flex gap-3"><MapPin className="text-[#C20E1A] mt-1" size={18} /> {COMPANY.address}</p>
              <p className="flex gap-3"><Phone className="text-[#C20E1A] mt-1" size={18} /> {COMPANY.phone}</p>
              <p className="flex gap-3"><Mail className="text-[#C20E1A] mt-1" size={18} /> <a className="link-red" href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a></p>
            </div>
            <div className="mt-8 flex items-center gap-3">
              <a
                href={COMPANY.facebook}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                data-testid="about-facebook"
                className="h-11 w-11 inline-flex items-center justify-center border border-black/15 hover:bg-[#C20E1A] hover:border-[#C20E1A] hover:text-white transition-colors"
              >
                <Facebook size={16} />
              </a>
              <a
                href={COMPANY.instagram}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                data-testid="about-instagram"
                className="h-11 w-11 inline-flex items-center justify-center border border-black/15 hover:bg-[#C20E1A] hover:border-[#C20E1A] hover:text-white transition-colors"
              >
                <Instagram size={16} />
              </a>
              <a
                href={COMPANY.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                data-testid="about-linkedin"
                className="h-11 w-11 inline-flex items-center justify-center border border-black/15 hover:bg-[#C20E1A] hover:border-[#C20E1A] hover:text-white transition-colors"
              >
                <Linkedin size={16} />
              </a>
            </div>
            <Link
              to={route("contact")}
              data-testid="about-contact-btn"
              className="mt-10 inline-block bg-[#C20E1A] text-white px-9 py-4 text-[11px] uppercase tracking-[0.25em] font-bold hover:bg-black transition-colors duration-500"
            >
              {t.nav.contact}
            </Link>
          </Reveal>
          <Reveal delay={120} className="aspect-[4/3] w-full overflow-hidden border border-black/10">
            <iframe
              title="Artful Contemporary location"
              src="https://www.google.com/maps?q=Anton-Kux-Str.+2,+41460+Neuss,+Germany&output=embed"
              className="w-full h-full grayscale-[40%] contrast-110"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </Reveal>
        </div>
      </section>
    </div>
  );
}
